import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { usePolling } from '@/hooks/usePolling';

import { ChatProgressWebSocket } from './ChatProgressWebSocket';
import type {
  ChainCompletedEvent,
  ChainFailedEvent,
  ChatProgressWebSocketStatus,
  StepProgressEvent,
  TitleUpdatedEvent,
} from './types';

const POLLING_INTERVAL_FAST_MS = 500;
const POLLING_INTERVAL_DEGRADED_MS = 30000;

interface UseChatProgressOptions {
  orgId: string | undefined;
  conversationId: string | null;
  taskId: string | null;
  onStepProgress: (event: StepProgressEvent) => void;
  onChainCompleted: (event: ChainCompletedEvent) => void;
  onChainFailed: (event: ChainFailedEvent) => void;
  onTitleUpdated: (event: TitleUpdatedEvent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polling data structure varies by task type
  onPollingUpdate: (data: any) => void;
  onPollingError: (error: string) => void;
  enabled?: boolean;
}

interface UseChatProgressReturn {
  wsStatus: ChatProgressWebSocketStatus;
  isPolling: boolean;
}

export function useChatProgress({
  orgId,
  conversationId,
  taskId,
  onStepProgress,
  onChainCompleted,
  onChainFailed,
  onTitleUpdated,
  onPollingUpdate,
  onPollingError,
  enabled = true,
}: UseChatProgressOptions): UseChatProgressReturn {
  const [wsStatus, setWsStatus] =
    useState<ChatProgressWebSocketStatus>('disconnected');
  const wsRef = useRef<ChatProgressWebSocket | null>(null);

  const stableOnStepProgress = useEffectEvent(onStepProgress);
  const stableOnChainCompleted = useEffectEvent(onChainCompleted);
  const stableOnChainFailed = useEffectEvent(onChainFailed);
  const stableOnTitleUpdated = useEffectEvent(onTitleUpdated);

  const pollingInterval =
    wsStatus === 'connected'
      ? POLLING_INTERVAL_DEGRADED_MS
      : POLLING_INTERVAL_FAST_MS;

  const { isPolling } = usePolling(taskId, conversationId, orgId, {
    enabled: !!enabled && !!taskId,
    interval: pollingInterval,
    onUpdate: onPollingUpdate,
    onError: onPollingError,
  });

  useEffect(() => {
    if (!enabled || !orgId || !conversationId) {
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
        setWsStatus('disconnected');
      }
      return;
    }

    const ws = new ChatProgressWebSocket({
      orgId,
      conversationId,
      onStepProgress: event => stableOnStepProgress(event),
      onChainCompleted: event => stableOnChainCompleted(event),
      onChainFailed: event => stableOnChainFailed(event),
      onTitleUpdated: event => stableOnTitleUpdated(event),
      onStatusChange: setWsStatus,
    });
    wsRef.current = ws;
    ws.connect();

    return () => {
      ws.destroy();
      wsRef.current = null;
      setWsStatus('disconnected');
    };
    // stableOnStepProgress/stableOnChainCompleted/stableOnChainFailed/
    // stableOnTitleUpdated are Effect Events (useEffectEvent) — stable by
    // design, must NOT be in the dep array (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, orgId, conversationId]);

  return { wsStatus, isPolling };
}
